import { TFunction } from 'react-i18next';

import { useMappedTranslation } from 'hooks/useMappedTranslation';

import { getMappedTranslation } from 'utility/Translate/mappedTranslation';

import { RoomType } from 'proto/Offer/Room/RoomType';

const roomTypeLabelTranslationKeyMap: Partial<Record<RoomType, string>> = {
    [RoomType.Apartment]: 'TRANS-room-type-apartment',
    [RoomType.Bungalow]: 'TRANS-room-type-bungalow',
    [RoomType.Caravan]: 'TRANS-room-type-caravan',
    [RoomType.Chalet]: 'TRANS-room-type-chalet',
    [RoomType.DeluxeBedRoom]: 'TRANS-room-type-deluxe',
    [RoomType.Disable]: 'TRANS-room-type-disable',
    [RoomType.DoubleBedRoom]: 'TRANS-room-type-double',
    [RoomType.DuplexBedRoom]: 'TRANS-room-type-duplex',
    [RoomType.Economy]: 'TRANS-room-type-economy',
    [RoomType.FamilyRoom]: 'TRANS-room-type-family',
    [RoomType.FourBedRoom]: 'TRANS-room-type-four-bed-room',
    [RoomType.HolidayFlat]: 'TRANS-room-type-holiday-flat',
    [RoomType.HolidayHouse]: 'TRANS-room-type-holiday-house',
    [RoomType.Houseboat]: 'TRANS-room-type-houseboat',
    [RoomType.JuniorSuite]: 'TRANS-room-type-junior-suite',
    [RoomType.Mobile]: 'TRANS-room-type-mobile',
    [RoomType.MultiShareRoom]: 'TRANS-room-type-multi-share',
    [RoomType.Program]: 'TRANS-room-type-program',
    [RoomType.SingleBedRoom]: 'TRANS-room-type-single',
    [RoomType.Studio]: 'TRANS-room-type-studio',
    [RoomType.Suite]: 'TRANS-room-type-suite',
    [RoomType.Superior]: 'TRANS-room-type-superior',
    [RoomType.TentPitch]: 'TRANS-room-type-campsite',
    [RoomType.TripleBedRoom]: 'TRANS-room-type-triple',
    [RoomType.TwinRoom]: 'TRANS-room-type-twin-room',
    [RoomType.RoomTypeUnknown]: 'TRANS-room',
    [RoomType.VacationHome]: 'TRANS-room-type-vacation-home',
    [RoomType.Villa]: 'TRANS-room-type-villa',
    [RoomType.WaterBungalow]: 'TRANS-room-type-water-bungalow',
};

const roomTypeTooltipTranslationKeyMap: Partial<Record<RoomType, string>> = {
    [RoomType.Apartment]: 'TRANS-room-type-tooltip-apartment',
    [RoomType.Bungalow]: 'TRANS-room-type-tooltip-bungalow',
    [RoomType.Caravan]: 'TRANS-room-type-tooltip-caravan',
    [RoomType.DeluxeBedRoom]: 'TRANS-room-type-tooltip-deluxe',
    [RoomType.Disable]: 'TRANS-room-type-tooltip-disable',
    [RoomType.DoubleBedRoom]: 'TRANS-room-type-tooltip-double',
    [RoomType.DuplexBedRoom]: 'TRANS-room-type-tooltip-duplex',
    [RoomType.Economy]: 'TRANS-room-type-tooltip-economy',
    [RoomType.FamilyRoom]: 'TRANS-room-type-tooltip-family',
    [RoomType.FourBedRoom]: 'TRANS-room-type-tooltip-four-bed-room',
    [RoomType.HolidayFlat]: 'TRANS-room-type-tooltip-holiday-flat',
    [RoomType.HolidayHouse]: 'TRANS-room-type-tooltip-holiday-house',
    [RoomType.Houseboat]: 'TRANS-room-type-tooltip-houseboat',
    [RoomType.JuniorSuite]: 'TRANS-room-type-tooltip-junior-suite',
    [RoomType.Mobile]: 'TRANS-room-type-tooltip-mobile',
    [RoomType.MultiShareRoom]: 'TRANS-room-type-tooltip-multi-share',
    [RoomType.Program]: 'TRANS-room-type-tooltip-program',
    [RoomType.SingleBedRoom]: 'TRANS-room-type-tooltip-single',
    [RoomType.Studio]: 'TRANS-room-type-tooltip-studio',
    [RoomType.Suite]: 'TRANS-room-type-tooltip-suite',
    [RoomType.Superior]: 'TRANS-room-type-tooltip-superior',
    [RoomType.TripleBedRoom]: 'TRANS-room-type-tooltip-triple',
    [RoomType.TwinRoom]: 'TRANS-room-type-tooltip-twin-room',
    [RoomType.RoomTypeUnknown]: 'TRANS-room-type-tooltip-program',
    [RoomType.VacationHome]: 'TRANS-room-type-tooltip-vacation-home',
    [RoomType.Villa]: 'TRANS-room-type-tooltip-villa',
    [RoomType.WaterBungalow]: 'TRANS-room-type-tooltip-water-bungalow',
};

const roomTypeDescriptionTranslationKeyMap: Partial<Record<RoomType, string>> =
    {
        [RoomType.Apartment]: 'TRANS-room-type-description-apartment',
        [RoomType.Bungalow]: 'TRANS-room-type-description-bungalow',
        [RoomType.Caravan]: 'TRANS-room-type-description-caravan',
        [RoomType.DeluxeBedRoom]: 'TRANS-room-type-description-deluxe',
        [RoomType.Disable]: 'TRANS-room-type-description-disable',
        [RoomType.DoubleBedRoom]: 'TRANS-room-type-description-double',
        [RoomType.DuplexBedRoom]: 'TRANS-room-type-description-duplex',
        [RoomType.Economy]: 'TRANS-room-type-description-economy',
        [RoomType.FamilyRoom]: 'TRANS-room-type-description-family',
        [RoomType.FourBedRoom]: 'TRANS-room-type-description-four-bed-room',
        [RoomType.HolidayFlat]: 'TRANS-room-type-description-holiday-flat',
        [RoomType.HolidayHouse]: 'TRANS-room-type-description-holiday-house',
        [RoomType.Houseboat]: 'TRANS-room-type-description-houseboat',
        [RoomType.JuniorSuite]: 'TRANS-room-type-description-junior-suite',
        [RoomType.Mobile]: 'TRANS-room-type-description-mobile',
        [RoomType.MultiShareRoom]: 'TRANS-room-type-description-multi-share',
        [RoomType.Program]: 'TRANS-room-type-program',
        [RoomType.SingleBedRoom]: 'TRANS-room-type-description-single',
        [RoomType.Studio]: 'TRANS-room-type-description-studio',
        [RoomType.Suite]: 'TRANS-room-type-description-suite',
        [RoomType.Superior]: 'TRANS-room-type-description-superior',
        [RoomType.TripleBedRoom]: 'TRANS-room-type-description-triple',
        [RoomType.TwinRoom]: 'TRANS-room-type-description-twin-room',
        [RoomType.RoomTypeUnknown]: 'TRANS-room',
        [RoomType.VacationHome]: 'TRANS-room-type-description-vacation-home',
        [RoomType.Villa]: 'TRANS-room-type-description-villa',
        [RoomType.WaterBungalow]: 'TRANS-room-type-description-water-bungalow',
    };

export const useRoomTypeLabel = (roomType: RoomType | undefined) =>
    useMappedTranslation(
        roomTypeLabelTranslationKeyMap,
        roomType,
        'TRANS-room-type-unknown'
    );

export const buildRoomTypeLabel = (
    roomType: RoomType | undefined,
    t: TFunction
) =>
    getMappedTranslation(
        roomTypeLabelTranslationKeyMap,
        roomType,
        t,
        'TRANS-room-type-unknown'
    );

export const useRoomTypeTooltip = (
    roomType: RoomType | undefined,
    roomClass = '',
    annexBuilding = '',
    roomDescription = ''
) =>
    useMappedTranslation(
        roomTypeTooltipTranslationKeyMap,
        roomType,
        'TRANS-room-type-tooltip-program',
        {
            roomClass,
            annexBuilding,
            roomDescription,
        }
    );

export const buildRoomTypeTooltip = (
    roomType: RoomType | undefined,
    t: TFunction,
    roomClass = '',
    annexBuilding = '',
    roomDescription = ''
) =>
    getMappedTranslation(
        roomTypeTooltipTranslationKeyMap,
        roomType,
        t,
        'TRANS-room-type-tooltip-program',
        {
            roomClass,
            annexBuilding,
            roomDescription,
        }
    );

export const useRoomTypeDescription = (roomType: RoomType | undefined) =>
    useMappedTranslation(
        roomTypeDescriptionTranslationKeyMap,
        roomType,
        'TRANS-room-type-program'
    );

export const buildRoomTypeDescription = (
    roomType: RoomType | undefined,
    t: TFunction
) =>
    getMappedTranslation(
        roomTypeDescriptionTranslationKeyMap,
        roomType,
        t,
        'TRANS-room-type-program'
    );
